/**
 * PDV-01 — Venta directa de mostrador.
 *
 * El flujo B2B de pedidos exige que OTRA persona apruebe antes de facturar
 * (`ApproveSalesOrderView` devuelve 403 al aprobar el pedido propio). En un
 * mostrador esa separación no existe: el vendedor cierra la venta con el cliente
 * enfrente. Este hook pega contra el endpoint que crea el pedido, el documento y
 * dispara la emisión en un solo paso.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type { CartItem } from '@/store/cartStore'

export interface PosCheckoutPayload {
  document_type: 'BE' | 'FE'
  /** Omitido en boleta = se emite al receptor genérico (CLIENTE BOLETA). */
  customer_id?: string
  lines: {
    sku: string
    name: string
    unit: string
    quantity: number
    unit_price: string
    price_currency: string
    is_tax_exempt: boolean
  }[]
  notes?: string
}

export interface PosCheckoutResult {
  document_id: string
  order_id: string
  order_number: string
  document_type: string
  customer: { id: string; legal_name: string; tax_id: string }
  subtotal: string
  tax_amount: string
  total: string
  status: string
  detail: string
}

/** Receptor genérico de boleta del tenant (SII: 66.666.666-6). */
export interface PosDefaultCustomer {
  id: string
  tax_id: string
  legal_name: string
}

export function cartToPosLines(items: CartItem[]): PosCheckoutPayload['lines'] {
  return items.map((i) => ({
    sku: i.sku,
    name: i.name,
    unit: i.unit,
    quantity: i.quantity,
    unit_price: i.unit_price,
    price_currency: i.price_currency,
    is_tax_exempt: i.is_tax_exempt,
  }))
}

export function usePosDefaultCustomer(enabled = true) {
  return useQuery<PosDefaultCustomer>({
    queryKey: ['pos', 'default-customer'],
    queryFn: async () => (await apiClient.get('/sales/pos/default-customer/')).data,
    enabled,
    staleTime: 1000 * 60 * 30,
  })
}

export function usePosCheckout() {
  const qc = useQueryClient()
  return useMutation<PosCheckoutResult, unknown, PosCheckoutPayload>({
    mutationFn: async (payload) =>
      (await apiClient.post('/sales/pos/checkout/', payload)).data,
    onSuccess: () => {
      // La venta mueve documentos, pedidos y stock a la vez.
      qc.invalidateQueries({ queryKey: ['sales'] })
      qc.invalidateQueries({ queryKey: ['inventory'] })
    },
  })
}
